import { Box, IconButton, Modal, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader/PageHeader';
import CustomTabs from '../../components/CustomTabs/CustomTabs';
import CustomAlert from '../../components/CustomAlert/CustomAlert';
import MasterDataTable from './MasterDataTable';
import MaterialSkuMasterTable from './MaterialSkuMasterTable';
import {
  fetchBlankFieldsValue,
  fetchCurrentTimeFrame,
  getDropdownListByFilterParam,
  fetchUploadsInProgress,
  fetchUploadsInProgressByUserId,
  fetchQualityItemsBannerValue
} from './services';
import CheckItemModalChild from './CheckItemModalChild';
import { useOkta } from '../../hooks/oktaHook';
import MasterDataQuickFill from './MasterDataQuickFill';
import { CORE_USER, TPO_ADMIN } from '../../utils/Constants';
import { getUrlWithParams } from '../../utils/Utils';
import { triggerNotification } from '../../utils/NotificationContext';

const tabs = [
  {
    id: 'masterData',
    name: 'PO SKU Master',
    showCycle: false,
    showTimeframe: true,
    showBu: true
  },
  {
    id: 'materialSkuMaster',
    name: 'Material SKU Master',
    showCycle: false,
    showTimeframe: true,
    showBu: true
  },
  {
    id: 'masterDataQuickFill',
    name: 'Master Data Quick Fill',
    showCycle: false,
    showTimeframe: true,
    showBu: true
  }
];

const findTabName = (id) => {
  const tab = tabs.find((tab) => tab.id === id);
  return tab || '';
};

const getInitialSelectedTab = (currentTab) => {
  const allowedTabs = tabs.map((tab) => tab.id);
  if (allowedTabs.includes(currentTab)) {
    return currentTab;
  }
  return allowedTabs[0];
};

const PortfolioMasterData = () => {
  const navigate = useNavigate();
  const intervalIdRef = useRef(null);
  const { userDetails } = useOkta();
  const { roles, email } = userDetails;
  const { search } = useLocation();
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const selectedBu = newParams.get('bu');
  // getting selected BU from homepage
  const portfolioMasterPage = useSelector((state) => state.portfolioMaster.portfolioMasterTabValue);
  const [selectedTab, setSelectedTab] = useState(getInitialSelectedTab(portfolioMasterPage));
  const cycleName = '';
  const [blankFieldsVal, setBlankFieldsVal] = useState(0);
  const qualityCheckData = {};
  const [isBannerCloseClicked, setIsBannerCloseClicked] = useState(false);
  const [selectedCheckModalOpen, setSelectedCheckModalOpen] = useState(false);
  const [refreshData, setRefreshData] = useState(0);
  const [timeframe, setTimeFrame] = useState({
    startTimePeriod: '',
    endTimePeriod: '',
    cycleName: ''
  });
  const [uploadStatusMsg, setUploadStatusMsg] = useState({});
  const [successFailureMsg, setSuccessFailureMsg] = useState({});
  const [criticalItemsMsg, setCriticalItemsMsg] = useState({});

  const handleCheckItems = () => {
    navigate(
      getUrlWithParams('/portfolio/check-items', selectedBu, selectedCycle, selectedCycleId),
      {
        state: {
          qualityCheckData,
          timeframe
        }
      }
    );
  };

  const getBlankFieldsValue = async (BuValue) => {
    try {
      const response = await fetchBlankFieldsValue(BuValue);

      setBlankFieldsVal(response);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  const getFileUploadStatus = async () => {
    //heartbeat
    try {
      let response = await fetchUploadsInProgress(selectedBu);
      setUploadStatusMsg(response);

      if (Object.entries(response).length == 0) {
        if (userDetails?.email) {
          getFileUploadStatusByUserID(userDetails?.email);
        }

        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null; // Clear the reference as well
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getFileUploadStatusByUserID = async (userEmail) => {
    try {
      const response = await fetchUploadsInProgressByUserId(userEmail, selectedBu);
      setUploadStatusMsg({});
      setSuccessFailureMsg(response);
      if (response) {
        if (response?.status === 'Success') {
          setRefreshData(refreshData+1);
          triggerNotification(response?.message + ' ' + response?.description, 'success');
        } else if (response?.status === 'Failed') {
          triggerNotification(response?.message + ' ' + response?.description, 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (selectedBu) {
      getBlankFieldsValue(selectedBu);
    }
  }, [selectedBu]);

  const fetchDropdownListByFilterParam = async () => {
    try {
      await getDropdownListByFilterParam(selectedBu);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (selectedBu) {
      fetchDropdownListByFilterParam();
    }
  }, []);

  useEffect(() => {
    if (userDetails?.email) {
      getFileUploadStatus();
    }
  }, [email]);

  useEffect(() => {
    if (selectedBu) {
      getCriticalItemsForCheckInData();
    }
  }, [selectedBu, refreshData]);

  useEffect(() => {
    setTimeFrame((prevTimeFrame) => ({
      ...prevTimeFrame,
      cycleName // update cycleName while preserving other properties
    }));
  }, [cycleName]);

  useEffect(() => {
    async function fetchData() {
      const timeFrameValue = 'L13'; // setting default value
      const response = await fetchCurrentTimeFrame(selectedBu, timeFrameValue);
      setTimeFrame({
        startTimePeriod: response?.startTimeFrame ? response.startTimeFrame : 'NA',
        endTimePeriod: response?.endTimeFrame ? response.endTimeFrame : 'NA'
      });
    }
    if (selectedBu) {
      fetchData();
    }
  }, [cycleName, selectedBu]);

  useEffect(() => {
    if (Object.entries(uploadStatusMsg).length) {
      // Only set interval if it's not already set
      if (!intervalIdRef.current) {
        intervalIdRef.current = setInterval(() => {
          getFileUploadStatus();
        }, 30000); // 30 seconds interval
      }
    } else {
      // If `uploadStatusMsg` is empty, clear the interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }
    // Cleanup: Clear the interval when the component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [uploadStatusMsg]); // Dependency array: re-run the effect when `uploadStatusMsg` changes

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40vw',
    height: 'auto',
    bgcolor: '#FBFCFE',
    boxShadow: '0px 2px 24px 8px #005CBC1F',
    padding: '32px 24px 24px 24px',
    border: '10px solid #D7E5F8',
    borderRadius: '8px'
  };
  const showCloseButton = true;

  const getCriticalItemsForCheckInData = async () => {
    try {
      const response = await fetchQualityItemsBannerValue(selectedBu);
      const criticalItems = {
        segment: "Segment",
        brandStrategy: "Brand Strategy",
        subCategory: "Sub Category"
      };

      const criticalItemsMsg = Object.keys(criticalItems)
        .filter(key => response[key] < 100)
        .map(key => criticalItems[key])
        .join(", ")
        .replace(/,([^,]*)$/, " &$1");
      setCriticalItemsMsg(criticalItemsMsg);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleAlertClose = () => {
    //call api to update the acknowledgement of user here
    setUploadStatusMsg({});
    setSuccessFailureMsg({});
    setIsBannerCloseClicked(true);
  };
  return (
    <Stack gap="24px" sx={{ padding: '24px', width: '100%' }}>
      {Object.entries(uploadStatusMsg).length == 0 &&
        Object.entries(successFailureMsg).length == 0 &&
        criticalItemsMsg.length > 0 &&
        roles?.some((role) => [CORE_USER, TPO_ADMIN].includes(role)) && (
          <CustomAlert
            titleMsg={`Some products require your attention`}
            detailMessage={`Ensure critical information such as ${criticalItemsMsg} for
            the SKUs before running your cycle.`}
            onCheckItems={handleCheckItems}
            showButton
            btnText="Review Items"
            color="#FFF2D2"
            textColor="#000000"
          />
        )}
      {/* show when upload is in progress */}
      {Object.entries(uploadStatusMsg).length !== 0 &&
        Object.entries(successFailureMsg).length == 0 && (
          <CustomAlert
            titleMsg={uploadStatusMsg.message || ''}
            detailMessage={uploadStatusMsg.description || ''}
            color="#FFF2D2"
            textColor="#000000"
          />
        )}

      {/* show when upload is successful/failed */}
      {Object.entries(successFailureMsg).length !== 0 &&
        Object.entries(uploadStatusMsg).length == 0 && (
          <CustomAlert
            titleMsg={successFailureMsg.message || ''}
            detailMessage={successFailureMsg.description || ''}
            onCheckItems={handleAlertClose}
            showButton
            btnText="Close"
            color="#FFF2D2"
            textColor="#000000"
          />
        )}

      <Modal
        open={selectedCheckModalOpen}
        onClose={() => setSelectedCheckModalOpen(false)}
        aria-labelledby={'abc'}
        aria-describedby={'abc'}>
        <Box sx={style}>
          {showCloseButton && (
            <IconButton
              size="small"
              onClick={() => setSelectedCheckModalOpen(false)}
              sx={{
                position: 'absolute',
                top: '-50px',
                right: '-10px',
                '&:hover': {
                  backgroundColor: '#B0C9E8'
                }
              }}>
              <CloseIcon fontSize="small" sx={{ color: 'white' }} />
            </IconButton>
          )}
          <CheckItemModalChild />
        </Box>
      </Modal>
      <PageHeader
        pageHeader={findTabName(selectedTab).name}
        cardHeaderDetails={timeframe}
        showCycle={findTabName(selectedTab).showCycle}
        showBu={findTabName(selectedTab).showBu}
        showTimeFrame={findTabName(selectedTab).showTimeframe}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '4px',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '4px',
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '3px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }
        }}></Box>
      {/* tab selection and cycle dropdown */}
      <Stack
        justifyContent={'space-between'}
        direction={'row'}
        flexWrap="wrap"
        sx={{
          alignItems: 'center',
          alignContent: 'center'
        }}>
        <CustomTabs
          tabs={tabs}
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          disabledTabs={
            roles?.some((role) => [CORE_USER, TPO_ADMIN].includes(role))
              ? []
              : ['masterDataQuickFill']
          }
        />
      </Stack>

      {/* actual data per tab */}
      {selectedTab === 'masterData' && (
        <MasterDataTable
          isBannerCloseClicked={isBannerCloseClicked}
          setBannerCloseFalse={() => setIsBannerCloseClicked(false)}
          setUploadStatusMsg={(data) => setUploadStatusMsg(data)}
          isUploadDownloadDisabled={
            Object.entries(uploadStatusMsg).length !== 0
          }
          refreshData={refreshData}
        />
      )}
      {selectedTab === 'materialSkuMaster' ? (
        <>
          <Box
            component="section"
            sx={{
              p: ['masterData', 'materialSkuMaster'].includes(selectedTab) ? 2 : 0,
              borderRadius: '8px',
              backgroundColor: ['masterData', 'materialSkuMaster'].includes(selectedTab)
                ? '#FBFCFE'
                : '',
              boxShadow: ['masterData', 'materialSkuMaster'].includes(selectedTab)
                ? '4px 0px 24px -2px #005CBC14'
                : ''
            }}>
            {selectedTab === 'materialSkuMaster' && <MaterialSkuMasterTable />}
          </Box>
        </>
      ) : (
        <></>
      )}
      {selectedTab === 'masterDataQuickFill' && <MasterDataQuickFill />}
    </Stack>
  );
};

export default PortfolioMasterData;
